import React, { useEffect, useState } from 'react';
import { Award, Trophy, Flame, TrendingDown, Loader2, Medal, Gauge, LineChart } from 'lucide-react';
import { PageHeader } from '../components/common';
import { useAuth } from '../context/AuthContext';
import { useRealtime } from '../realtime/RealtimeContext';
import { useI18n } from '../i18n/I18nContext';
import { awardIcon, awardColor } from '../utils/awardStyles';
import api from '../api/client';

const fmtMins = (m) => { m = Math.round(m || 0); return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`; };

export default function MyAwards() {
  const { user } = useAuth();
  const { t } = useI18n();
  const me = user?.name || '';
  const [data, setData] = useState({ badges: [], streaks: [], mostImproved: null });
  const [perf, setPerf] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    Promise.all([
      api.operatorBadges(me).then(setData).catch(() => {}),
      api.operatorPerformance('month').then(setPerf).catch(() => {}),
      api.performanceTrend(me, 6).then(setTrend).catch(() => {}),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [me]);
  useRealtime(() => load());

  const badges = data.badges || [];
  const customAwards = data.customAwards || [];
  const myStreak = (data.streaks || []).find((s) => s.operator === me) || null;
  const mostImproved = data.mostImproved && data.mostImproved.operator === me ? data.mostImproved : null;
  const perfRow = (perf?.operators || []).find((o) => o.operator === me);
  const perfRank = (perf?.operators || []).findIndex((o) => o.operator === me);
  const totalMedals = badges.length + customAwards.length;
  const trendMonths = trend?.months || [];
  const hasTrend = trendMonths.some((m) => m.score != null);
  const maxScore = 100;

  return (
    <div className="kmt-fade-in">
      <PageHeader icon={Award} title={t('nav.myAwards')} subtitle={t('awards.subtitle')} />

      <div className="p-8 space-y-6" data-testid="my-awards-page">
        {loading ? (
          <div className="flex items-center justify-center py-24" style={{ color: 'var(--kmt-muted)' }}><Loader2 className="animate-spin" size={28} /></div>
        ) : (
          <>
            {/* Summary strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="my-awards-summary">
              <div className="kmt-panel p-5 flex flex-col items-center text-center">
                <Trophy size={24} style={{ color: 'var(--kmt-gold)' }} />
                <div className="font-display font-bold mt-2" style={{ fontSize: 30, color: 'var(--kmt-gold)' }} data-testid="my-medal-count">{totalMedals}</div>
                <div className="kmt-label">{t('awards.medals')}</div>
              </div>
              <div className="kmt-panel p-5 flex flex-col items-center text-center">
                <Flame size={24} style={{ color: '#f0783a' }} />
                <div className="font-display font-bold mt-2" style={{ fontSize: 30, color: '#f0783a' }}>{myStreak ? myStreak.length : 0}</div>
                <div className="kmt-label">{t('awards.streakMonths')}</div>
              </div>
              <div className="kmt-panel p-5 flex flex-col items-center text-center">
                <Gauge size={24} style={{ color: 'var(--kmt-green)' }} />
                <div className="font-display font-bold mt-2" style={{ fontSize: 30, color: perfRow ? 'var(--kmt-green)' : 'var(--kmt-muted-2)' }}>{perfRow ? perfRow.score : '—'}</div>
                <div className="kmt-label">{t('awards.perfScore')}</div>
              </div>
              <div className="kmt-panel p-5 flex flex-col items-center text-center">
                <Medal size={24} style={{ color: '#c0c4cc' }} />
                <div className="font-display font-bold mt-2" style={{ fontSize: 30 }}>{perfRank >= 0 ? `#${perfRank + 1}` : '—'}</div>
                <div className="kmt-label">{t('awards.rankMonth')}</div>
              </div>
            </div>

            {/* Performance score trend */}
            <div className="kmt-panel p-6" data-testid="my-performance-trend">
              <div className="flex items-center gap-2 mb-4">
                <LineChart size={17} style={{ color: 'var(--kmt-green)' }} />
                <div className="font-display font-semibold">{t('awards.trendTitle')}</div>
              </div>
              {!hasTrend ? (
                <div className="text-center py-8 text-sm" style={{ color: 'var(--kmt-muted)' }} data-testid="my-trend-empty">{t('awards.trendEmpty')}</div>
              ) : (
                <div className="flex items-end gap-3" style={{ height: 160 }}>
                  {trendMonths.map((m) => (
                    <div key={m.month} className="flex-1 flex flex-col items-center gap-1" data-testid={`trend-bar-${m.month}`}>
                      <span className="font-mono-k" style={{ fontSize: 11, color: m.score != null ? 'var(--kmt-green)' : 'var(--kmt-muted-2)' }}>{m.score != null ? m.score : '–'}</span>
                      <div className="w-full flex items-end" style={{ height: 110 }}>
                        <div className="w-full rounded-t" style={{ height: `${m.score != null ? (m.score / maxScore) * 110 : 0}px`, minHeight: m.score != null ? 3 : 0, background: m.score != null ? 'var(--kmt-green)' : 'transparent' }}
                          title={`${m.label}: ${m.score != null ? m.score + '/100' : t('awards.noScore')}${m.rank ? ` · #${m.rank}` : ''}`} />
                      </div>
                      <span className="text-xs" style={{ color: 'var(--kmt-muted)' }}>{m.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Streak + most improved highlight */}
            {(myStreak || mostImproved) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {myStreak && (
                  <div className="kmt-panel p-4 flex items-center gap-3" data-testid="my-streak-card" style={{ borderColor: 'rgba(240,120,43,0.5)' }}>
                    <div className="flex items-center justify-center rounded-full shrink-0" style={{ width: 42, height: 42, background: 'rgba(240,120,43,0.14)' }}>
                      <Flame size={22} style={{ color: '#f0783a' }} />
                    </div>
                    <div>
                      <div className="font-display font-semibold">{t('awards.streakTitle').replace('{n}', myStreak.length)}</div>
                      <div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>{t('awards.streakDesc').replace('{months}', myStreak.months.join(' → '))}</div>
                    </div>
                  </div>
                )}
                {mostImproved && (
                  <div className="kmt-panel p-4 flex items-center gap-3" data-testid="my-most-improved-card" style={{ borderColor: 'rgba(63,185,120,0.5)' }}>
                    <div className="flex items-center justify-center rounded-full shrink-0" style={{ width: 42, height: 42, background: 'rgba(63,185,120,0.14)' }}>
                      <TrendingDown size={22} style={{ color: 'var(--kmt-green)' }} />
                    </div>
                    <div>
                      <div className="font-display font-semibold">{t('awards.mostImproved')}</div>
                      <div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>{t('awards.mostImprovedDesc').replace('{imp}', fmtMins(mostImproved.improvement)).replace('{prev}', fmtMins(mostImproved.previousAvg)).replace('{cur}', fmtMins(mostImproved.currentAvg))}</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Special awards from managers */}
            {customAwards.length > 0 && (
              <div className="kmt-panel p-6" data-testid="my-special-awards">
                <div className="flex items-center gap-2 mb-4">
                  <Award size={17} style={{ color: 'var(--kmt-gold)' }} />
                  <div className="font-display font-semibold">{t('awards.specialTitle')}</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {customAwards.map((a) => {
                    const Ico = awardIcon(a.icon);
                    return (
                      <div key={a.id} className="flex items-center gap-3 rounded-lg px-4 py-3" data-testid={`my-special-award-${a.id}`}
                        style={{ background: 'var(--kmt-panel-2)', border: `1px solid ${awardColor(a.color)}66` }}>
                        <div className="flex items-center justify-center rounded-full shrink-0" style={{ width: 44, height: 44, background: `${awardColor(a.color)}22` }}>
                          <Ico size={22} style={{ color: awardColor(a.color) }} />
                        </div>
                        <div className="min-w-0">
                          <div className="font-display font-medium truncate" style={{ color: awardColor(a.color) }}>{a.title}</div>
                          {a.description && <div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>{a.description}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Medals collection */}
            <div className="kmt-panel p-6" data-testid="my-medals-collection">
              <div className="flex items-center gap-2 mb-4">
                <Trophy size={17} style={{ color: 'var(--kmt-gold)' }} />
                <div className="font-display font-semibold">{t('awards.medalsTitle')}</div>
              </div>
              {badges.length === 0 ? (
                <div className="text-center py-10" style={{ color: 'var(--kmt-muted)' }} data-testid="my-awards-empty">
                  <Medal size={30} className="mx-auto mb-3" style={{ color: 'var(--kmt-muted-2)' }} />
                  {t('awards.empty')}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {badges.map((b) => (
                    <div key={b.month} className="flex items-center gap-3 rounded-lg px-4 py-3" data-testid={`my-medal-${b.month}`}
                      style={{ background: 'var(--kmt-panel-2)', border: `1px solid ${b.current ? 'var(--kmt-gold)' : 'var(--kmt-border)'}` }}>
                      <div className="flex items-center justify-center rounded-full shrink-0" style={{ width: 44, height: 44, background: 'rgba(240,169,43,0.12)' }}>
                        <Trophy size={22} style={{ color: 'var(--kmt-gold)' }} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-display font-medium">{t('awards.fastest')} · {b.monthLabel}</div>
                        <div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>{t('awards.medalMeta').replace('{avg}', fmtMins(b.avgMinutes)).replace('{n}', b.sessions)}{b.current ? ` · ${t('awards.current')}` : ''}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
